import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Loading from "../elements/Loading";
import InlineNotification from "../elements/InlineNotification";
import ImportanceSelector from "../elements/inputs/ImportanceSelector";
import OneSelectUI from "../elements/inputs/one-select";
import ConstraintTemplatePicker from "../elements/ConstraintTemplatePicker";
import getAPIClient from "../hooks/apiClient";
import s from '../styles/pages/VacancySettingsPage.module.scss';
import { FaPlus, FaTrash } from "react-icons/fa";


const DEFAULT_IMPORTANCE = 'acceptable';


// Принимаем и старый формат (string), и новый ({text, importance}).
function normalizeItemFromServer(raw) {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'string') return { text: raw, importance: DEFAULT_IMPORTANCE };
    if (typeof raw === 'object' && raw.text !== undefined) {
        return { text: raw.text || '', importance: raw.importance || DEFAULT_IMPORTANCE };
    }
    return null;
}

function normalizeListFromServer(list) {
    if (!Array.isArray(list) || list.length === 0) {
        return [{ text: '', importance: DEFAULT_IMPORTANCE }];
    }
    return list.map(normalizeItemFromServer).filter(Boolean);
}


export default function VacancySettingsPage({ BX24, vacancyItemId }) {
    const api = useMemo(() => getAPIClient(BX24), []);
    const [isLoading, setIsLoading] = useState(true);
    const [notificationText, setNotificationText] = useState('');
    const [notificationType, setNotificationType] = useState('error');
    const [crmItemId, setCrmItemId] = useState(null);
    const [vacancyData, setVacancyData] = useState({
        name: '',
        description: '',
        competencies: [{ text: '', importance: DEFAULT_IMPORTANCE }],
        requirements: [{ text: '', importance: DEFAULT_IMPORTANCE }],
    });
    const [showRequirementsErrors, setShowRequirementsErrors] = useState(false);

    // HH-фильтр импорта по этапу
    const [hhImportEnabled, setHhImportEnabled] = useState(false);
    const [hhImportStateId, setHhImportStateId] = useState('');
    const [hhStages, setHhStages] = useState([]);
    const [hhStagesLoading, setHhStagesLoading] = useState(false);
    const [hhStagesAvailable, setHhStagesAvailable] = useState(true);
    const [hhStagesHint, setHhStagesHint] = useState('');

    const hhStageOptions = useMemo(
        () => hhStages.map((stage) => ({ value: String(stage.id), label: stage.name })),
        [hhStages]
    );
    const selectedHhStageOption = useMemo(
        () => hhStageOptions.find((opt) => opt.value === hhImportStateId) || null,
        [hhStageOptions, hhImportStateId]
    );

    // Синхронизация переходов по этапам HH <-> Bitrix
    const [stageSyncEnabled, setStageSyncEnabled] = useState(false);
    const [stageSyncSendTemplate, setStageSyncSendTemplate] = useState(true); // слать шаблонное письмо при переносе на HH
    const [stageMappings, setStageMappings] = useState([]); // [{hh_stage_id, b24_stage_id}]
    const [b24Stages, setB24Stages] = useState([]); // [{stage_id, stage_name, is_final}]
    const [b24StagesLoading, setB24StagesLoading] = useState(false);

    const b24StageOptions = useMemo(
        () => b24Stages.map((st) => ({
            value: String(st.stage_id),
            label: `${st.stage_name}${st.is_final ? ' (финальный)' : ''}`,
        })),
        [b24Stages]
    );

    // Этап нельзя выбрать дважды: импорт и пары переноса делят общий пул HH-этапов,
    // внутри пар каждый HH- и Bitrix-этап уникален. Текущее значение строки всегда
    // оставляем в списке, чтобы выбранное не пропадало из селекта.
    const hhImportOptions = useMemo(() => {
        const usedInMappings = new Set(stageMappings.map((m) => m.hh_stage_id).filter(Boolean));
        return hhStageOptions.filter((o) => o.value === hhImportStateId || !usedInMappings.has(o.value));
    }, [hhStageOptions, stageMappings, hhImportStateId]);

    const hhStageOptionsForRow = (idx, current) => {
        const used = new Set(stageMappings.filter((_, i) => i !== idx).map((m) => m.hh_stage_id).filter(Boolean));
        if (hhImportEnabled && hhImportStateId) used.add(hhImportStateId);
        return hhStageOptions.filter((o) => o.value === current || !used.has(o.value));
    };

    const b24StageOptionsForRow = (idx, current) => {
        const used = new Set(stageMappings.filter((_, i) => i !== idx).map((m) => m.b24_stage_id).filter(Boolean));
        return b24StageOptions.filter((o) => o.value === current || !used.has(o.value));
    };

    const addStageMapping = () => setStageMappings((p) => [...p, { hh_stage_id: '', b24_stage_id: '' }]);
    const removeStageMapping = (idx) => setStageMappings((p) => p.filter((_, i) => i !== idx));
    const updateStageMapping = (idx, field, value) =>
        setStageMappings((p) => p.map((m, i) => (i === idx ? { ...m, [field]: value } : m)));

    // Пикер шаблонов
    const [templatePicker, setTemplatePicker] = useState({ open: false });

    // Для автофокуса на последнем добавленном поле
    const competenciesContainerRef = useRef(null);
    const requirementsContainerRef = useRef(null);
    const focusLastInput = (container) => {
        if (!container) return;
        const inputs = container.querySelectorAll('input[type="text"]');
        const last = inputs[inputs.length - 1];
        if (last) {
            last.focus();
            last.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    };

    const handleResponseError = useCallback((defaultText, e) => {
        console.error('Error:', e);
        setNotificationType('error');
        if (e?.response?.data?.detail) {
            setNotificationText(`${e.response.data.detail}`);
        } else {
            setNotificationText(defaultText);
        }
    }, []);

    const fetchVacancyData = useCallback(async () => {
        if (!crmItemId) return;
        setIsLoading(true);
        try {
            const response = await api.get(`/analysis_agent/vacancies?crm_item_id=${crmItemId}`);
            if (response.data) {
                setVacancyData({
                    name: response.data.name || '',
                    description: response.data.description || '',
                    competencies: normalizeListFromServer(response.data.competencies),
                    requirements: normalizeListFromServer(response.data.requirements),
                });
                setHhImportEnabled(Boolean(response.data.hh_import_enabled));
                const stateIds = Array.isArray(response.data.hh_import_state_ids)
                    ? response.data.hh_import_state_ids
                    : [];
                setHhImportStateId(stateIds.length ? String(stateIds[0]) : '');
                setStageSyncEnabled(Boolean(response.data.stage_sync_enabled));
                setStageSyncSendTemplate(response.data.stage_sync_send_template !== false);
                const mappings = Array.isArray(response.data.stage_mappings) ? response.data.stage_mappings : [];
                setStageMappings(mappings.map((m) => ({
                    hh_stage_id: String(m.hh_stage_id ?? ''),
                    b24_stage_id: String(m.b24_stage_id ?? ''),
                })));
            }
        } catch (e) {
            handleResponseError('Ошибка загрузки данных вакансии', e);
        } finally {
            setIsLoading(false);
        }
    }, [api, crmItemId, handleResponseError]);

    const fetchHhStages = useCallback(async () => {
        if (!crmItemId) return;
        setHhStagesLoading(true);
        try {
            const response = await api.get(`/analysis_agent/vacancies/hh/states?crm_item_id=${crmItemId}`);
            const list = Array.isArray(response.data) ? response.data : [];
            setHhStages(list);
            setHhStagesAvailable(true);
            setHhStagesHint(list.length ? '' : 'HH не авторизован или у вакансии нет этапов отбора');
        } catch (e) {
            if (e?.response?.status === 422) {
                setHhStages([]);
                setHhStagesAvailable(false);
                setHhStagesHint('Вакансия не связана с HH — импорт с конкретного этапа недоступен');
            } else {
                setHhStages([]);
                setHhStagesAvailable(true);
                setHhStagesHint('Не удалось загрузить список этапов HH');
            }
        } finally {
            setHhStagesLoading(false);
        }
    }, [api, crmItemId]);

    // B24-этапы воронки откликов (дефолтное направление)
    const fetchB24Stages = useCallback(async () => {
        setB24StagesLoading(true);
        try {
            const dirsResp = await api.get('/analysis_agent/applicant/directions');
            const dirs = Array.isArray(dirsResp.data) ? dirsResp.data : [];
            const defaultDir = dirs.find((d) => d.is_default) || dirs[0];
            const dirId = defaultDir ? defaultDir.direction_id : 0;
            const stagesResp = await api.get(`/analysis_agent/applicant/stages?direction_id=${dirId}`);
            setB24Stages(Array.isArray(stagesResp.data) ? stagesResp.data : []);
        } catch (e) {
            setB24Stages([]);
        } finally {
            setB24StagesLoading(false);
        }
    }, [api]);

    const saveVacancySettings = useCallback(async () => {
        const hasEmptyRequirements = vacancyData.requirements.some(r => !r.text.trim());
        if (hasEmptyRequirements) {
            setShowRequirementsErrors(true);
            setNotificationType('error');
            setNotificationText('Заполните все поля ограничений перед сохранением');
            return;
        }
        if (hhImportEnabled && !hhImportStateId) {
            setNotificationType('error');
            setNotificationText('Выберите этап HH для импорта откликов или отключите фильтр');
            return;
        }
        if (stageSyncEnabled) {
            if (stageMappings.length === 0 || stageMappings.some((m) => !m.hh_stage_id || !m.b24_stage_id)) {
                setNotificationType('error');
                setNotificationText('Заполните оба этапа в каждой паре синхронизации или отключите синхронизацию');
                return;
            }
        }
        setShowRequirementsErrors(false);
        setIsLoading(true);
        try {
            const dataToSend = {
                name: vacancyData.name,
                description: vacancyData.description,
                competencies: vacancyData.competencies
                    .filter((c) => c.text.trim() !== '')
                    .map((c) => ({ text: c.text, importance: c.importance || DEFAULT_IMPORTANCE })),
                requirements: vacancyData.requirements
                    .filter((r) => r.text.trim() !== '')
                    .map((r) => ({ text: r.text, importance: r.importance || DEFAULT_IMPORTANCE })),
                hh_import_enabled: hhImportEnabled,
                hh_import_state_ids: hhImportStateId ? [hhImportStateId] : [],
                stage_sync_enabled: stageSyncEnabled,
                stage_sync_send_template: stageSyncSendTemplate,
                stage_mappings: stageSyncEnabled
                    ? stageMappings.filter((m) => m.hh_stage_id && m.b24_stage_id)
                    : [],
            };

            await api.patch(`/analysis_agent/vacancies?crm_item_id=${crmItemId}`, dataToSend);
            setNotificationType('success');
            setNotificationText('Настройки успешно сохранены');
        } catch (e) {
            handleResponseError('Ошибка сохранения настроек', e);
        } finally {
            setIsLoading(false);
        }
    }, [api, crmItemId, handleResponseError, vacancyData, hhImportEnabled, hhImportStateId, stageSyncEnabled, stageSyncSendTemplate, stageMappings]);

    useEffect(() => {
        const init = async () => {
            if (vacancyItemId) {
                setCrmItemId(vacancyItemId);
                return;
            }
            try {
                const placement = await BX24.placement.info();
                const itemId = placement?.options?.ID || placement?.options?.ENTITY_ID;
                if (itemId) {
                    setCrmItemId(itemId);
                } else {
                    setNotificationType('error');
                    setNotificationText('Не удалось получить ID вакансии');
                    setIsLoading(false);
                }
            } catch (e) {
                handleResponseError('Ошибка инициализации', e);
                setIsLoading(false);
            }
        };
        init();
    }, [BX24, handleResponseError, vacancyItemId]);

    useEffect(() => {
        if (crmItemId) {
            fetchVacancyData();
            fetchHhStages();
            fetchB24Stages();
        }
    }, [crmItemId, fetchVacancyData, fetchHhStages, fetchB24Stages]);

    // Field handlers
    const handleNameChange = (e) => setVacancyData((p) => ({ ...p, name: e.target.value }));
    const handleDescriptionChange = (e) => setVacancyData((p) => ({ ...p, description: e.target.value }));

    // Competencies
    const updateCompetency = (index, patch) => {
        setVacancyData((prev) => {
            const next = [...prev.competencies];
            next[index] = { ...next[index], ...patch };
            return { ...prev, competencies: next };
        });
    };
    const addCompetency = () => {
        setVacancyData((prev) => ({
            ...prev,
            competencies: [...prev.competencies, { text: '', importance: DEFAULT_IMPORTANCE }],
        }));
        requestAnimationFrame(() => focusLastInput(competenciesContainerRef.current));
    };
    const removeCompetency = (index) => {
        setVacancyData((prev) => {
            if (prev.competencies.length <= 1) return prev;
            const next = [...prev.competencies];
            next.splice(index, 1);
            return { ...prev, competencies: next };
        });
    };

    // Requirements
    const updateRequirement = (index, patch) => {
        setVacancyData((prev) => {
            const next = [...prev.requirements];
            next[index] = { ...next[index], ...patch };
            return { ...prev, requirements: next };
        });
    };
    const addRequirement = () => {
        setVacancyData((prev) => ({
            ...prev,
            requirements: [...prev.requirements, { text: '', importance: DEFAULT_IMPORTANCE }],
        }));
        requestAnimationFrame(() => focusLastInput(requirementsContainerRef.current));
    };
    const removeRequirement = (index) => {
        setVacancyData((prev) => {
            if (prev.requirements.length <= 1) return prev;
            const next = [...prev.requirements];
            next.splice(index, 1);
            return { ...prev, requirements: next };
        });
    };

    const openTemplatePicker = () => setTemplatePicker({ open: true });
    const closeTemplatePicker = () => setTemplatePicker({ open: false });
    const applyTemplate = ({ text }) => {
        setVacancyData((prev) => {
            const next = [...prev.requirements];
            const lastIndex = next.length - 1;
            const newItem = { text, importance: DEFAULT_IMPORTANCE };
            if (lastIndex >= 0 && !next[lastIndex].text.trim()) {
                next[lastIndex] = newItem;
            } else {
                next.push(newItem);
            }
            return { ...prev, requirements: next };
        });
        requestAnimationFrame(() => focusLastInput(requirementsContainerRef.current));
    };

    return (
        <div className={s.container}>
            <div className={s.mainBlock}>
                {notificationText && (
                    <InlineNotification
                        type={notificationType}
                        text={notificationText}
                        hideFlag={notificationText.length === 0}
                    />
                )}

                {isLoading ? (
                    <Loading isLoading={true} />
                ) : (
                    <div className={s.form}>
                        <div className={s.formGroup}>
                            <label htmlFor="name">Название вакансии</label>
                            <input
                                type="text"
                                id="name"
                                value={vacancyData.name}
                                onChange={handleNameChange}
                                placeholder="Введите название"
                                className={s.input}
                            />
                        </div>

                        <div className={s.formGroup}>
                            <label htmlFor="description">Описание вакансии</label>
                            <textarea
                                id="description"
                                value={vacancyData.description}
                                onChange={handleDescriptionChange}
                                placeholder="Введите описание вакансии"
                                rows="4"
                                className={s.textarea}
                            />
                        </div>

                        {/* HH import stage filter */}
                        <div className={s.formGroup}>
                            <div className={s.hhSyncRow}>
                                <label className={s.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={hhImportEnabled}
                                        onChange={(e) => setHhImportEnabled(e.target.checked)}
                                        disabled={!hhStagesAvailable}
                                    />
                                    Импортировать отклики только с выбранного этапа HH
                                </label>
                            </div>

                            {hhImportEnabled && hhStagesAvailable && (
                                <div className={s.hhStageBlock}>
                                    <OneSelectUI
                                        title="Этап импорта"
                                        selectionOptions={hhImportOptions}
                                        selectedOption={selectedHhStageOption}
                                        setSelectedOption={(opt) => setHhImportStateId(opt ? opt.value : '')}
                                        isLoading={hhStagesLoading}
                                        isDisabled={hhStagesLoading || hhStages.length === 0}
                                        placeholder="Выберите этап…"
                                        isClearable
                                    />
                                    <div className={s.listFooter}>
                                        <button
                                            type="button"
                                            className={s.secondaryButton}
                                            onClick={fetchHhStages}
                                            disabled={hhStagesLoading}
                                        >
                                            {hhStagesLoading ? 'Загружаю…' : 'Обновить список'}
                                        </button>
                                    </div>
                                    {hhStagesHint && <p className={s.hintText}>{hhStagesHint}</p>}

                                    {/* Toggle B — синхронизация переходов по этапам */}
                                    <div className={s.hhSyncRow} style={{ marginTop: 14 }}>
                                        <label className={s.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={stageSyncEnabled}
                                                onChange={(e) => {
                                                    const on = e.target.checked;
                                                    setStageSyncEnabled(on);
                                                    if (on && stageMappings.length === 0) addStageMapping();
                                                }}
                                            />
                                            Синхронизировать переход по этапам
                                        </label>
                                    </div>

                                    {stageSyncEnabled && (
                                        <div className={s.stageMapBlock}>
                                            {stageMappings.length > 0 && (
                                                <div className={s.stageMapHeader}>
                                                    <span>Этап HH</span>
                                                    <span>Этап Bitrix</span>
                                                    <span className={s.stageMapHeaderSpacer} />
                                                </div>
                                            )}
                                            {stageMappings.map((m, idx) => (
                                                <div key={idx} className={s.stageMapRow}>
                                                    <OneSelectUI
                                                        selectionOptions={hhStageOptionsForRow(idx, m.hh_stage_id)}
                                                        selectedOption={hhStageOptions.find((o) => o.value === m.hh_stage_id) || null}
                                                        setSelectedOption={(opt) => updateStageMapping(idx, 'hh_stage_id', opt ? opt.value : '')}
                                                        placeholder="Этап HH…"
                                                        isClearable
                                                    />
                                                    <OneSelectUI
                                                        selectionOptions={b24StageOptionsForRow(idx, m.b24_stage_id)}
                                                        selectedOption={b24StageOptions.find((o) => o.value === m.b24_stage_id) || null}
                                                        setSelectedOption={(opt) => updateStageMapping(idx, 'b24_stage_id', opt ? opt.value : '')}
                                                        isLoading={b24StagesLoading}
                                                        placeholder="Этап Bitrix…"
                                                        isClearable
                                                    />
                                                    <button
                                                        type="button"
                                                        className={s.removeButton}
                                                        onClick={() => removeStageMapping(idx)}
                                                        title="Удалить пару"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            ))}
                                            <div className={s.listFooter}>
                                                <button type="button" className={s.secondaryButton} onClick={addStageMapping}>
                                                    + Добавить пару
                                                </button>
                                            </div>

                                            <div className={s.hhSyncRow} style={{ marginTop: 12 }}>
                                                <label className={s.checkboxLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={stageSyncSendTemplate}
                                                        onChange={(e) => setStageSyncSendTemplate(e.target.checked)}
                                                    />
                                                    При переносе этапа на HH отправлять шаблонное сообщение
                                                </label>
                                            </div>
                                            <p className={s.mutedHint}>
                                                При отключении сообщения отправляться не будут — может понизиться лояльность на HH.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!hhImportEnabled && hhStagesHint && (
                                <p className={s.hintText}>{hhStagesHint}</p>
                            )}
                        </div>

                        {/* Competencies */}
                        <div className={s.formGroup}>
                            <div className={s.listHeader}>
                                <label>Компетенции — список требуемых hard-skills</label>
                            </div>

                            <div className={s.listItems} ref={competenciesContainerRef}>
                                {vacancyData.competencies.map((competency, index) => (
                                    <div key={index} className={s.listItem}>
                                        <input
                                            type="text"
                                            value={competency.text}
                                            onChange={(e) => updateCompetency(index, { text: e.target.value })}
                                            placeholder={`Компетенция ${index + 1}`}
                                            className={s.input}
                                        />
                                        <ImportanceSelector
                                            value={competency.importance}
                                            onChange={(importance) => updateCompetency(index, { importance })}
                                        />
                                        <button
                                            type="button"
                                            className={s.removeButton}
                                            onClick={() => removeCompetency(index)}
                                            disabled={vacancyData.competencies.length <= 1}
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className={s.listFooter}>
                                <button type="button" className={s.addButton} onClick={addCompetency}>
                                    <FaPlus /> Добавить компетенцию
                                </button>
                            </div>
                        </div>

                        {/* Requirements / constraints */}
                        <div className={s.formGroup}>
                            <div className={s.listHeader}>
                                <label>
                                    Ограничения — негативные фильтры, пример: проживает в Екатеринбурге
                                    {showRequirementsErrors && vacancyData.requirements.some(r => !r.text.trim()) && (
                                        <span className={s.requiredMark}> * заполните все поля</span>
                                    )}
                                </label>
                            </div>

                            <div className={s.listItems} ref={requirementsContainerRef}>
                                {vacancyData.requirements.map((requirement, index) => {
                                    const isEmpty = showRequirementsErrors && !requirement.text.trim();
                                    return (
                                    <div key={index} className={s.listItem}>
                                        <input
                                            type="text"
                                            value={requirement.text}
                                            onChange={(e) => updateRequirement(index, { text: e.target.value })}
                                            placeholder={isEmpty ? '* Обязательное поле' : `Ограничение ${index + 1}`}
                                            className={`${s.input}${isEmpty ? ` ${s.inputError}` : ''}`}
                                        />
                                        <ImportanceSelector
                                            value={requirement.importance}
                                            onChange={(importance) => updateRequirement(index, { importance })}
                                        />
                                        <button
                                            type="button"
                                            className={s.removeButton}
                                            onClick={() => removeRequirement(index)}
                                            disabled={vacancyData.requirements.length <= 1}
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                    );
                                })}
                            </div>

                            <div className={s.listFooter}>
                                <button type="button" className={s.addButton} onClick={addRequirement}>
                                    <FaPlus /> Добавить ограничение
                                </button>
                                <button
                                    type="button"
                                    className={s.addButton}
                                    onClick={openTemplatePicker}
                                >
                                    <FaPlus /> Добавить из шаблона
                                </button>
                            </div>
                        </div>

                        <div className={s.actions}>
                            <button
                                className={s.primaryButton}
                                onClick={saveVacancySettings}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Сохранение…' : 'Сохранить настройки'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {templatePicker.open && (
                <ConstraintTemplatePicker
                    BX24={BX24}
                    onPick={applyTemplate}
                    onClose={closeTemplatePicker}
                />
            )}
        </div>
    );
}